/**
 * @file コンポーネント：フル動画ページダイジェスト動画 ロジック管理
 */

// ライブラリ読み込み
import React, { useEffect, useState } from "react";

// Presenter読み込み
import { Presenter } from "./presenter";

// 関数読み込み
import {
    fnConvertSecondToTimeString,
    fnHandleApiErrors,
    fnHandleApiSuccess,
    fnPushDataLayerGtm,
} from "~/src/functions/common";

// コンポーネントを返す
export const DigestPlayer = (props) => {
    /**
     * ダイジェスト動画読み込み
     */
    const [fairplayCer, setFairplayCer] = useState(null); // fairplay.cerのパス
    const [hasAudio, setHasAudio] = useState(false); // ダイジェスト動画の音声有無
    const [isLoaded, setIsLoaded] = useState(false); // ダイジェスト動画の読み込み完了フラグ
    const [movieUrl, setMovieUrl] = useState(null); // ダイジェスト動画URL
    const [fairplayLaUrl, setFairplayLaUrl] = useState(null); // fairplayライセンスURL
    const [playreadyLaUrl, setPlayreadyLaUrl] = useState(null); // playreadyライセンスURL
    const [widevineLaUrl, setWidevineLaUrl] = useState(null); // widevineライセンスURL
    useEffect(() => {
        const api = "/api/Digests/" + props.operationId;
        fetch(api)
            .then(fnHandleApiErrors)
            .then(fnHandleApiSuccess)
            .then((result) => {
                if (result !== null) {
                    setFairplayCer(result.fairplayCer);
                    setHasAudio(result.digestVideo.hasAudio);
                    setMovieUrl(result.digestVideo.videoLocateUrl);
                    setIsLoaded(true);
                    setFairplayLaUrl(result.fairplayLaUrl);
                    setPlayreadyLaUrl(result.playreadyLaUrl);
                    setWidevineLaUrl(result.widevineLaUrl);
                }
            })
            .catch((error) => {
                console.error(error);
            });
    }, []);

    /**
     * mediaPlayerの処理
     */
    const [mediaPlayerDigestvideo, setMediaPlayerDigestvideo] = useState(null); // ダイジェスト動画のMedia Player
    const [isPlay, setIsPlay] = useState(false); // Media Playerの再生フラグ
    const [timeProgressSecond, setTimeProgressSecond] = useState(0); // 再生時間（秒）
    const [timeTotalSecond, setTimeTotalSecond] = useState(0); // 総時間（秒）
    useEffect(() => {
        if (mediaPlayerDigestvideo !== null) {
            // ミュート設定
            if (!hasAudio) mediaPlayerDigestvideo.muted = true;

            // 再生状態切り替え処理（一時停止）
            mediaPlayerDigestvideo.addEventListener("pause", () => {
                setIsPlay(false);
            });
            // 再生状態切り替え処理（再生）
            mediaPlayerDigestvideo.addEventListener("play", () => {
                setIsPlay(true);
            });
            // 再生時間の取得処理
            mediaPlayerDigestvideo.addEventListener("timeupdate", () => {
                setTimeProgressSecond(mediaPlayerDigestvideo.currentTime);
            });
            // 総時間の取得処理
            mediaPlayerDigestvideo.addEventListener(
                "timeupdate",
                () => {
                    setTimeTotalSecond(mediaPlayerDigestvideo.duration);
                },
                { once: true }
            );
            // Google Tag Manager カスタムイベントトリガー
            //// ダイジェスト動画が最後まで再生された
            mediaPlayerDigestvideo.addEventListener(
                "ended",
                () => {
                    fnPushDataLayerGtm({
                        event: "CompleteDigest",
                        operationId: window.atob(props.operationId),
                        userId: window.dataUser.userId,
                    });
                },
                { once: true }
            );
        }
    }, [mediaPlayerDigestvideo]);
    // 再生進捗に応じた処理
    const [isSentProgress25, setIsSentProgress25] = useState(false); // ダイジェスト動画が全体の25%地点を過ぎたことがあるかどうか
    const [isSentProgress50, setIsSentProgress50] = useState(false); // ダイジェスト動画が全体の50%地点を過ぎたことがあるかどうか
    const [isSentProgress75, setIsSentProgress75] = useState(false); // ダイジェスト動画が全体の75%地点を過ぎたことがあるかどうか
    useEffect(() => {
        if (
            isPlay === true &&
            timeProgressSecond !== 0 &&
            timeTotalSecond !== 0
        ) {
            // Google Tag Manager カスタムイベントトリガー
            //// ダイジェスト動画が全体の25%地点を過ぎた
            if (
                timeProgressSecond >= timeTotalSecond / 4 &&
                isSentProgress25 === false
            ) {
                fnPushDataLayerGtm({
                    event: "ProgressDigest25",
                    operationId: window.atob(props.operationId),
                    userId: window.dataUser.userId,
                    video_parcent: 25,
                    video_progress: fnConvertSecondToTimeString(
                        Math.ceil(timeTotalSecond / 4)
                    ),
                });
                setIsSentProgress25(true);
            }
            //// ダイジェスト動画が全体の50%地点を過ぎた
            if (
                timeProgressSecond >= timeTotalSecond / 2 &&
                isSentProgress50 === false
            ) {
                fnPushDataLayerGtm({
                    event: "ProgressDigest50",
                    operationId: window.atob(props.operationId),
                    userId: window.dataUser.userId,
                    video_parcent: 50,
                    video_progress: fnConvertSecondToTimeString(
                        Math.ceil(timeTotalSecond / 2)
                    ),
                });
                setIsSentProgress50(true);
            }
            //// ダイジェスト動画が全体の75%地点を過ぎた
            if (
                timeProgressSecond >= timeTotalSecond * 0.75 &&
                isSentProgress75 === false
            ) {
                fnPushDataLayerGtm({
                    event: "ProgressDigest75",
                    operationId: window.atob(props.operationId),
                    userId: window.dataUser.userId,
                    video_parcent: 75,
                    video_progress: fnConvertSecondToTimeString(
                        Math.ceil(timeTotalSecond * 0.75)
                    ),
                });
                setIsSentProgress75(true);
            }
        }
    }, [
        isPlay,
        isSentProgress25,
        isSentProgress50,
        isSentProgress75,
        timeProgressSecond,
        timeTotalSecond,
    ]);

    /**
     * 初回再生時のみの処理
     */
    const [isPlayed, setIsPlayed] = useState(false); // 初回再生済みかどうか
    useEffect(() => {
        if (
            mediaPlayerDigestvideo !== null &&
            isPlay === true &&
            isPlayed === false
        ) {
            // Google Tag Manager カスタムイベントトリガー
            //// スポンサーコンテンツのダイジェスト動画が再生された
            if (props.isSponsorContent) {
                fnPushDataLayerGtm({
                    event: "PlayCountDigestVideo",
                    operationId: window.atob(props.operationId),
                    userId: window.dataUser.userId,
                });
            }
            //// ダイジェスト動画が再生された
            fnPushDataLayerGtm({
                event: "PlayDigest",
                operationId: window.atob(props.operationId),
                userId: window.dataUser.userId,
            });
            // 初回再生フラグをtrueに
            setIsPlayed(true);
        }
    }, [mediaPlayerDigestvideo, isPlay]);

    /**
     * 動画タブ切り替え時に再生中なら一時停止する
     */
    useEffect(() => {
        if (isPlay === true && props.selectedContentsData !== "digestvideo") {
            mediaPlayerDigestvideo.pause();
        }
    }, [props.selectedContentsData]);

    // Presenterに渡す
    return (
        <Presenter
            fairplayCer={fairplayCer}
            fairplayLaUrl={fairplayLaUrl}
            hasAudio={hasAudio}
            isLoaded={isLoaded}
            movieUrl={movieUrl}
            playreadyLaUrl={playreadyLaUrl}
            setMediaPlayerDigestvideo={setMediaPlayerDigestvideo}
            widevineLaUrl={widevineLaUrl}
        />
    );
};
